import {
  DEFAULT_LOCALE,
  getDirection,
  getLocale as getParalocale,
  languageTagToLocale,
  localeToLanguageTag,
  type SupportedLocale,
  setLocale as setParalocale,
} from '@pricedrop/i18n';
import * as React from 'react';

type I18nContextValue = {
  locale: SupportedLocale;
  direction: 'ltr' | 'rtl';
  setLocale: (locale: SupportedLocale) => void;
};

const I18nContext = React.createContext<I18nContextValue | null>(null);

export function useI18n() {
  const context = React.useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return context;
}

interface I18nProviderProps {
  children: React.ReactNode;
}

function getCurrentLocale(): SupportedLocale {
  try {
    const paraLocale = getParalocale();
    return languageTagToLocale(paraLocale as 'en' | 'he');
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function I18nProvider({ children }: I18nProviderProps) {
  const [locale, setLocaleState] = React.useState<SupportedLocale>(getCurrentLocale);

  const direction = getDirection(locale);

  const setLocale = React.useCallback(
    (newLocale: SupportedLocale) => {
      if (newLocale !== locale) {
        const langTag = localeToLanguageTag(newLocale);
        setParalocale(langTag, { reload: false });
        if (typeof document !== 'undefined') {
          document.documentElement.lang = newLocale;
          document.documentElement.dir = getDirection(newLocale);
        }
        setLocaleState(newLocale);
      }
    },
    [locale],
  );

  const value = React.useMemo(
    () => ({ locale, direction, setLocale }),
    [locale, direction, setLocale],
  );

  return (
    <I18nContext.Provider value={value}>
      <React.Fragment key={locale}>{children}</React.Fragment>
    </I18nContext.Provider>
  );
}

export function useLocale() {
  const { locale, setLocale } = useI18n();
  return [locale, setLocale] as const;
}
